import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ChevronDown, X } from "lucide-react";
import { sidebarSections } from "./SidebarLinksData";
import { useAccess } from "../../access/AccessContext";
import TechFrigateLogo from "../Common/TechFrigateLogo";

const itemBase =
  "group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors";
const itemIdle = "text-slate-300 hover:bg-white/5 hover:text-white";
const itemActive = "bg-brand-500 text-white shadow-sm shadow-brand-900/40";

const Sidebar = ({ isOpen, onClose }) => {
  const { can, access } = useAccess();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const sidebarRef = useRef(null);

  const sections = useMemo(
    () =>
      sidebarSections
        .map((section) => ({
          ...section,
          links: section.links
            .map((link) =>
              link.subLink ? { ...link, subLink: link.subLink.filter((s) => can(s.permission)) } : link
            )
            .filter((link) => (link.subLink ? link.subLink.length > 0 : can(link.permission))),
        }))
        .filter((section) => section.links.length > 0),
    [can]
  );

  const groupForPath = (path) =>
    sidebarSections.flatMap((s) => s.links).find((l) => l.subLink?.some((s) => s.route === path))?.name;
  const [openGroup, setOpenGroup] = useState(() => groupForPath(pathname));

  useEffect(() => {
    const group = groupForPath(pathname);
    if (group) setOpenGroup(group);
  }, [pathname]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  const onNavigate = () => {
    dispatch({ payload: false, type: "Change_Show_Form_State" });
    if (window.innerWidth < 1024) onClose();
  };

  return (
    <aside
      ref={sidebarRef}
      className={`fixed inset-y-0 left-0 z-50 w-64 shrink-0 transition-transform duration-300 lg:static lg:z-0 lg:translate-x-0 ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <div className="flex h-full flex-col bg-navy-900">
        <div className="flex h-16 items-center justify-between gap-3 border-b border-white/10 px-5">
          <div className="flex items-center gap-3">
            <svg viewBox="0 0 40 40" className="h-9 w-9 shrink-0" aria-hidden="true"><defs><linearGradient id="hf-central" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#2f7bff" /><stop offset="1" stopColor="#10b991" /></linearGradient></defs><rect width="40" height="40" rx="11" fill="url(#hf-central)" /><path d="M17 10h6v7h7v6h-7v7h-6v-7h-7v-6h7z" fill="#fff" /></svg>
            <div className="leading-tight">
              <p className="text-sm font-semibold text-white">Health Frigate Central</p>
              <p className="text-xs text-slate-400">Health network console</p>
            </div>
          </div>
          <button className="rounded-md p-1 text-slate-400 hover:bg-white/10 lg:hidden" onClick={onClose} aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="customScrollbar flex-1 space-y-6 overflow-y-auto px-3 py-5">
          {sections.map((section) => (
            <div key={section.title}>
              <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{section.title}</p>
              <div className="space-y-0.5">
                {section.links.map((link) => {
                  const Icon = link.icon;
                  if (!link.subLink) {
                    return (
                      <NavLink
                        key={link.name}
                        to={link.route}
                        onClick={onNavigate}
                        className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemIdle}`}
                      >
                        <Icon className="h-[18px] w-[18px] shrink-0" />
                        {link.name}
                      </NavLink>
                    );
                  }
                  const expanded = openGroup === link.name;
                  const childActive = link.subLink.some((s) => s.route === pathname);
                  return (
                    <div key={link.name}>
                      <button
                        type="button"
                        onClick={() => setOpenGroup(expanded ? null : link.name)}
                        className={`${itemBase} ${childActive ? "text-white" : itemIdle}`}
                        aria-expanded={expanded}
                      >
                        <Icon className={`h-[18px] w-[18px] shrink-0 ${childActive ? "text-brand-300" : ""}`} />
                        <span className="flex-1 text-left">{link.name}</span>
                        <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${expanded ? "rotate-180" : ""}`} />
                      </button>
                      {expanded && (
                        <div className="ml-[22px] mt-0.5 space-y-0.5 border-l border-white/10 pl-3">
                          {link.subLink.map((sub) => (
                            <NavLink
                              key={sub.route}
                              to={sub.route}
                              onClick={onNavigate}
                              className={({ isActive }) =>
                                `block rounded-md px-3 py-1.5 text-sm transition-colors ${
                                  isActive ? "bg-white/10 font-medium text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"
                                }`
                              }
                            >
                              {sub.name}
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {access && sections.length === 0 && (
            <p className="px-3 text-sm text-slate-400">Your role has no modules assigned yet.</p>
          )}
        </nav>

        <div className="border-t border-white/10 px-3 py-3 [&_*]:!text-slate-400">
          <TechFrigateLogo />
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
